import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { AirLineViewModel } from '../models/AirLineViewModel';
import type { AirportSearchViewModel } from '../models/AirportSearchViewModel';
import type { FlightBookOrderViewModel } from '../models/FlightBookOrderViewModel';
import type { FlightListViewModel } from '../models/FlightListViewModel';
import type { FlightPriceRequestViewModel } from '../models/FlightPriceRequestViewModel';
import type { FlightPriceResponseModel } from '../models/FlightPriceResponseModel';
import type { FlightServiceFeeRequestViewModel } from '../models/FlightServiceFeeRequestViewModel';
import type { FlightServiceFeeResponseViewModel } from '../models/FlightServiceFeeResponseViewModel';
import type { FlightStatusViewModel } from '../models/FlightStatusViewModel';
import type { FligthtPnrDetailViewModel } from '../models/FligthtPnrDetailViewModel';
import type { LandingPagesFlightViewModel } from '../models/LandingPagesFlightViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class FlightsService {
  /**
   * List
   * Gets all flights
   * @returns FlightListViewModel Success
   * @throws ApiError
   */
  public static async listFlights(
    {
      departure,
      departureIsCity,
      arrival,
      arrivalIsCity,
      departureDate,
      returnDate,
      adt,
      chd,
      inf,
      direct,
      oneWay,
      domestic,
      refundable,
      priceMin,
      priceMax,
      cabins,
      transfers,
      airlines,
      times,
      page,
      size,
    }: {
      departure?: string;
      departureIsCity?: boolean;
      arrival?: string;
      arrivalIsCity?: boolean;
      departureDate?: string;
      returnDate?: string;
      adt?: string;
      chd?: string;
      inf?: string;
      direct?: boolean;
      oneWay?: boolean;
      domestic?: boolean;
      refundable?: boolean;
      priceMin?: number;
      priceMax?: number;
      cabins?: Array<string>;
      transfers?: Array<number>;
      airlines?: Array<string>;
      times?: Array<number>;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightListViewModel>({
      method: 'GET',
      url: `/flights`,
      query: {
        ...(departure ? { departure: departure } : {}),
        ...(departureIsCity ? { departureIsCity: departureIsCity } : {}),
        ...(arrival ? { arrival: arrival } : {}),
        ...(arrivalIsCity ? { arrivalIsCity: arrivalIsCity } : {}),
        ...(departureDate ? { departureDate: departureDate } : {}),
        ...(returnDate ? { returnDate: returnDate } : {}),
        ...(adt ? { adt: adt } : {}),
        ...(chd ? { chd: chd } : {}),
        ...(inf ? { inf: inf } : {}),
        ...(direct ? { direct: direct } : {}),
        ...(oneWay ? { oneWay: oneWay } : {}),
        ...(domestic ? { domestic: domestic } : {}),
        ...(refundable ? { refundable: refundable } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(cabins ? { cabins: cabins } : {}),
        ...(transfers ? { transfers: transfers } : {}),
        ...(airlines ? { airlines: airlines } : {}),
        ...(times ? { times: times } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * @deprecated
   * Search
   * Searches airports or locations
   * @returns AirportSearchViewModel Success
   * @throws ApiError
   */
  public static async searchAirport(
    {
      s,
    }: {
      /**
       * Search terms
       */
      s: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<AirportSearchViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<AirportSearchViewModel>>({
      method: 'GET',
      url: `/flights/search`,
      query: {
        ...(s ? { s: s } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Rules
   * Gets flight rules
   * @returns string Success
   * @throws ApiError
   */
  public static async getRules(
    {
      token,
      body,
    }: {
      token: string;
      body?: FlightBookOrderViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/flights/${token}/rules`,
      path: {
        ...(token ? { token: token } : {}),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Fee
   * Gets service fee for flights
   * @returns FlightServiceFeeResponseViewModel Success
   * @throws ApiError
   */
  public static async getServiceFee(
    {
      body,
    }: {
      body?: FlightServiceFeeRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightServiceFeeResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightServiceFeeResponseViewModel>({
      method: 'POST',
      url: `/flights/fee`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Price
   * Gets price for flights
   * @returns FlightPriceResponseModel Success
   * @throws ApiError
   */
  public static async getPrice(
    {
      body,
    }: {
      body?: FlightPriceRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightPriceResponseModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightPriceResponseModel>({
      method: 'POST',
      url: `/flights/price`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Airlines
   * Gets airline list
   * @returns AirLineViewModel Success
   * @throws ApiError
   */
  public static async listAirLines(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<AirLineViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<AirLineViewModel>>({
      method: 'GET',
      url: `/flights/airlines`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (Landing Page)
   * Gets flights for landing pages
   * @returns LandingPagesFlightViewModel Success
   * @throws ApiError
   */
  public static async getLandingPageFlights(
    {
      origin,
      destination,
      date,
    }: {
      origin?: string;
      destination?: string;
      date?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<LandingPagesFlightViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<LandingPagesFlightViewModel>>({
      method: 'GET',
      url: `/flights/landing`,
      query: {
        ...(origin ? { origin: origin } : {}),
        ...(destination ? { destination: destination } : {}),
        ...(date ? { date: date } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (PNR Details)
   * Get flight pnr details
   * @returns FligthtPnrDetailViewModel Success
   * @throws ApiError
   */
  public static async getflightPnrDetails(
    {
      pnr,
      lastName,
    }: {
      pnr: string;
      lastName: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FligthtPnrDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FligthtPnrDetailViewModel>({
      method: 'GET',
      url: `/flights/pnr-details`,
      query: {
        ...(pnr ? { pnr: pnr } : {}),
        ...(lastName ? { lastName: lastName } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (Flight Status)
   * Get flight status by pnr
   * @returns FlightStatusViewModel Success
   * @throws ApiError
   */
  public static async getFlightStatus(
    {
      pnr,
      lastName,
    }: {
      pnr: string;
      lastName: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightStatusViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightStatusViewModel>({
      method: 'GET',
      url: `/flights/status`,
      query: {
        ...(pnr ? { pnr: pnr } : {}),
        ...(lastName ? { lastName: lastName } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
